
import { Globe, Smartphone } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TableInfoText } from "./TableInfoText";
import { SystemWideRulesTable } from "./SystemWideRulesTable";
import { AppSpecificRulesTable } from "./AppSpecificRulesTable";
import { type NetworkRule } from "@/hooks/useNetworkRules";
import { type GroupedRule } from "./NetworkRulesUtils";

interface NetworkRulesTabsProps {
  currentFilterTab: string;
  handleFilterTabChange: (tab: string) => void;
  systemWideCount: number;
  appSpecificCount: number;
  getFilteredGroups: (filterType: string) => GroupedRule[];
  expandedApps: Set<string>;
  toggleAppExpansion: (bundleId: string) => void;
  onRuleClick: (rule: NetworkRule) => void;
}

export const NetworkRulesTabs = ({
  currentFilterTab,
  handleFilterTabChange,
  systemWideCount,
  appSpecificCount,
  getFilteredGroups,
  expandedApps,
  toggleAppExpansion,
  onRuleClick,
}: NetworkRulesTabsProps) => {
  return (
    <div className="space-y-4">
      <Tabs value={currentFilterTab} onValueChange={handleFilterTabChange} className="space-y-4">
        <div className="border-b border-gray-200">
          <TabsList className="grid grid-cols-2 bg-transparent h-auto p-0 rounded-none">
            <TabsTrigger 
              value="system-wide"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 rounded-none py-3 px-4 text-sm font-medium"
            >
              <Globe className="w-4 h-4 mr-2" />
              System-wide ({systemWideCount})
            </TabsTrigger>
            <TabsTrigger 
              value="app-specific"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-blue-500 data-[state=active]:text-blue-600 rounded-none py-3 px-4 text-sm font-medium"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              App Specific ({appSpecificCount})
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="system-wide" className="space-y-6">
          <TableInfoText text="These rules apply across all apps. Bundle IDs can be specified to target specific components." />
          <SystemWideRulesTable 
            filteredGroups={getFilteredGroups("system-wide")} 
            onRuleClick={onRuleClick} 
          />
        </TabsContent>

        <TabsContent value="app-specific" className="space-y-6">
          <TableInfoText text="These rules only apply within the apps that they are displayed in." />
          <AppSpecificRulesTable 
            filteredGroups={getFilteredGroups("app-specific")}
            expandedApps={expandedApps}
            toggleAppExpansion={toggleAppExpansion}
            onRuleClick={onRuleClick}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
